/**
 * Modal layers of the game screen: staff menu (Esc), field manual, settings, hotkey card,
 * surrender confirmation and — once the war is decided — the after-action report.
 */
import type { ReactNode } from 'react';
import { bus } from '../../game/bus';
import { formatDate } from '../../game/format';
import { gameStore, useGame } from '../../game/store';
import { useT } from '../../i18n';
import { getConnection } from '../../net/connection';
import { lobbyStore } from '../../net/lobby';
import { Button } from '../common/controls';
import { Paper } from '../common/Panel';
import { FieldManualContent } from '../screens/FieldManual';
import { SettingsContent } from '../screens/Settings';
import { AfterActionReport } from './AfterAction';
import { hotkeys } from './hotkeys';
import hud from './hud.module.css';
import { hudStore, useHud } from './hudStore';
import o from './overlays.module.css';

const close = () => hudStore.getState().setOverlay(null);

function StaffMenu() {
  const t = useT();
  const hud$ = hudStore.getState();
  const tick = useGame((g) => g.view?.tick ?? 0);
  const single = getConnection()?.kind === 'local';
  return (
    <div className={o.backdrop} onClick={close}>
      <section className={`${hud.plate} ${o.menu}`} onClick={(e) => e.stopPropagation()}>
        <div className={o.menuHead}>
          <div className={o.menuKicker}>{t('staff.kicker')}</div>
          <div className={o.menuTitle}>{t('staff.title')}</div>
          <div className={o.menuSub}>
            {formatDate(tick, t.locale)}
            {single ? t('staff.clockStopped') : ''}
          </div>
        </div>
        <div className={o.menuList}>
          <Button variant="primary" block icon="play" hotkey="Esc" onClick={close}>
            {t('staff.return')}
          </Button>
          <div className={o.menuSep} />
          <Button block icon="book" onClick={() => hud$.setOverlay('manual')}>
            {t('staff.manual')}
          </Button>
          <Button block icon="keyboard" onClick={() => hud$.setOverlay('hotkeys')}>
            {t('staff.hotkeys')}
          </Button>
          <Button block icon="gear" onClick={() => hud$.setOverlay('settings')}>
            {t('staff.settings')}
          </Button>
          <div className={o.menuSep} />
          <Button block variant="danger" icon="white" onClick={() => hud$.setOverlay('surrender')}>
            {t('staff.capitulate')}
          </Button>
          <Button block variant="ghost" icon="exit" onClick={() => lobbyStore.getState().quitGame()}>
            {single ? t('staff.abandon') : t('staff.leave')}
          </Button>
        </div>
      </section>
    </div>
  );
}

function Sheet({ title, children }: { title: string; children: ReactNode }) {
  const t = useT();
  return (
    <div className={o.backdrop} onClick={() => hudStore.getState().setOverlay('menu')}>
      <section className={`${hud.plate} ${o.sheet}`} onClick={(e) => e.stopPropagation()}>
        <header className={o.sheetHead}>
          <Button
            variant="ghost"
            icon="chevronLeft"
            hotkey="Esc"
            onClick={() => hudStore.getState().setOverlay('menu')}
          >
            {t('ui.back')}
          </Button>
          <span className={o.sheetTitle}>{title}</span>
        </header>
        <div className={o.sheetBody}>{children}</div>
      </section>
    </div>
  );
}

function HotkeyCard() {
  const t = useT();
  return (
    <div className={o.backdrop} onClick={close}>
      <Paper className={o.keys}>
        <div className={o.keysTitle}>{t('keys.title')}</div>
        {hotkeys(t).map((k) => (
          <div key={k.label} className={o.keyRow}>
            <span>{k.label}</span>
            <span className={o.caps}>
              {k.keys.map((c) =>
                c === '–' ? (
                  <span key={c}>–</span>
                ) : (
                  <kbd key={c} className={o.cap}>
                    {c}
                  </kbd>
                ),
              )}
            </span>
          </div>
        ))}
        <div className={o.keysFoot}>{t('keys.foot')}</div>
      </Paper>
    </div>
  );
}

function SurrenderConfirm() {
  const t = useT();
  return (
    <div className={o.backdrop} onClick={() => hudStore.getState().setOverlay('menu')}>
      <Paper className={o.confirm}>
        <div onClick={(e) => e.stopPropagation()}>
          <div className={o.confirmTitle}>{t('surrender.title')}</div>
          <p className={o.confirmText}>{t('surrender.text')}</p>
          <div className={o.confirmBtns}>
            <Button onClick={() => hudStore.getState().setOverlay('menu')}>{t('surrender.fight')}</Button>
            <Button
              variant="danger"
              icon="white"
              onClick={() => {
                gameStore.getState().sendCommand({ type: 'surrender' });
                bus.emit('sound', { name: 'stamp' });
                close();
              }}
            >
              {t('surrender.capitulate')}
            </Button>
          </div>
        </div>
      </Paper>
    </div>
  );
}

export function Overlays() {
  const t = useT();
  const overlay = useHud((h) => h.overlay);
  const over = useGame((g) => g.view?.winners != null);
  if (over) return <AfterActionReport />;
  switch (overlay) {
    case 'menu':
      return <StaffMenu />;
    case 'manual':
      return (
        <Sheet title={t('sheet.manual')}>
          <FieldManualContent />
        </Sheet>
      );
    case 'settings':
      return (
        <Sheet title={t('sheet.settings')}>
          <SettingsContent />
        </Sheet>
      );
    case 'hotkeys':
      return <HotkeyCard />;
    case 'surrender':
      return <SurrenderConfirm />;
    default:
      return null;
  }
}
